import React, { useEffect, useState } from 'react';
import { Loader2, FileText, CheckCircle2, Phone, Star, ChevronDown, ChevronUp, AlertCircle, ThumbsUp, ThumbsDown, Sparkles } from 'lucide-react';
import { clientApi, formatCurrency } from '../../components/client-portal/clientPortalApi';

const STATUS_BADGES = {
  sent: { label: 'Awaiting your decision', cls: 'bg-amber-50 text-amber-800 ring-amber-200' },
  call_requested: { label: 'Call requested', cls: 'bg-sky-50 text-sky-700 ring-sky-200' },
  approved: { label: 'Approved', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  declined: { label: 'Declined', cls: 'bg-rose-50 text-rose-700 ring-rose-200' },
};

export default function ClientProposals() {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const reload = async () => {
    const ps = await clientApi('/client/proposals');
    setProposals(ps);
    if (ps.length > 0 && !openId) setOpenId(ps[0].id);
  };

  useEffect(() => {
    (async () => { try { await reload(); } finally { setLoading(false); } })();
    // eslint-disable-next-line
  }, []);

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-6" data-testid="client-proposals">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900">Your loan options</h1>
        <p className="text-slate-500 mt-1 text-sm">Review the options your broker has prepared. Approve the one that suits you, or request a call to talk it through.</p>
      </div>

      {proposals.length === 0 && (
        <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-10 text-center">
          <FileText className="w-10 h-10 mx-auto text-slate-300" />
          <div className="mt-3 text-slate-500 text-sm">No proposals yet — we'll let you know as soon as your broker has prepared your options.</div>
        </div>
      )}

      <div className="space-y-4">
        {proposals.map((p) => (
          <ProposalCard
            key={p.id}
            proposal={p}
            open={openId === p.id}
            onToggle={() => setOpenId(openId === p.id ? null : p.id)}
            onChanged={reload}
          />
        ))}
      </div>
    </div>
  );
}

function ProposalCard({ proposal, open, onToggle, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [callOpen, setCallOpen] = useState(false);
  const [callNotes, setCallNotes] = useState('');
  const [callTime, setCallTime] = useState('');
  const [confirmId, setConfirmId] = useState(null);

  const status = proposal.status || 'sent';
  const badge = STATUS_BADGES[status] || STATUS_BADGES.sent;
  const isLocked = status === 'approved' || status === 'declined';

  const approve = async () => {
    if (!confirmId) return;
    setBusy(true);
    try {
      await clientApi(`/client/proposals/${proposal.id}/approve`, {
        method: 'POST', body: { selected_option_id: confirmId }
      });
      await onChanged?.();
      setConfirmId(null);
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  };

  const requestCall = async () => {
    setBusy(true);
    try {
      await clientApi(`/client/proposals/${proposal.id}/request-call`, {
        method: 'POST', body: { notes: callNotes, preferred_time: callTime }
      });
      await onChanged?.();
      setCallOpen(false);
      setCallNotes('');
      setCallTime('');
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  };

  return (
    <article className="rounded-2xl bg-white ring-1 ring-slate-200 overflow-hidden" data-testid={`proposal-${proposal.id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-slate-50/50"
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-semibold text-slate-900 truncate">{proposal.title}</h2>
            <span className={`px-2 py-0.5 rounded-full ring-1 text-[11px] ${badge.cls}`}>{badge.label}</span>
          </div>
          {proposal.broker_summary && <p className="text-sm text-slate-500 mt-1">{proposal.broker_summary}</p>}
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
      </button>

      {open && (
        <div className="border-t border-slate-100 p-5 space-y-5">
          {(proposal.why_this_suits_goals || proposal.risks_notes) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {proposal.why_this_suits_goals && (
                <div className="rounded-xl bg-emerald-50/60 ring-1 ring-emerald-200 p-4">
                  <div className="flex items-center gap-2 text-emerald-700 text-xs font-semibold uppercase tracking-wide mb-1">
                    <Sparkles className="w-3.5 h-3.5" /> Why this suits you
                  </div>
                  <div className="text-sm text-slate-700 whitespace-pre-wrap">{proposal.why_this_suits_goals}</div>
                </div>
              )}
              {proposal.risks_notes && (
                <div className="rounded-xl bg-amber-50/60 ring-1 ring-amber-200 p-4">
                  <div className="flex items-center gap-2 text-amber-800 text-xs font-semibold uppercase tracking-wide mb-1">
                    <AlertCircle className="w-3.5 h-3.5" /> Risks &amp; important notes
                  </div>
                  <div className="text-sm text-slate-700 whitespace-pre-wrap">{proposal.risks_notes}</div>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {(proposal.options || []).map((o) => (
              <OptionCard
                key={o.id}
                option={o}
                isSelected={proposal.selected_option_id === o.id}
                disabled={isLocked || busy}
                onApprove={() => setConfirmId(o.id)}
              />
            ))}
          </div>

          {!isLocked && (
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                type="button"
                onClick={() => setCallOpen(true)}
                disabled={busy}
                data-testid={`request-call-${proposal.id}`}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl ring-1 ring-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-medium"
              >
                <Phone className="w-4 h-4" />
                Request a call before approving
              </button>
            </div>
          )}

          {callOpen && (
            <div className="rounded-xl ring-1 ring-slate-200 p-4 bg-slate-50">
              <div className="font-medium text-slate-800 mb-2">Request a call</div>
              <input
                type="text"
                value={callTime}
                onChange={(e) => setCallTime(e.target.value)}
                placeholder="When works for you? (e.g. Tomorrow afternoon)"
                data-testid="call-time-input"
                className="w-full px-3 py-2 rounded-lg ring-1 ring-slate-200 bg-white text-sm mb-2"
              />
              <textarea
                rows={3}
                value={callNotes}
                onChange={(e) => setCallNotes(e.target.value)}
                placeholder="Anything you'd like to discuss?"
                data-testid="call-notes-input"
                className="w-full px-3 py-2 rounded-lg ring-1 ring-slate-200 bg-white text-sm"
              />
              <div className="mt-2 flex justify-end gap-2">
                <button onClick={() => setCallOpen(false)} className="text-sm text-slate-500 px-3 py-1.5">Cancel</button>
                <button
                  onClick={requestCall}
                  disabled={busy}
                  data-testid="confirm-call-request"
                  className="inline-flex items-center gap-2 px-4 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium"
                >
                  {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                  Send request
                </button>
              </div>
            </div>
          )}

          {confirmId && (
            <ApproveConfirm
              option={(proposal.options || []).find((o) => o.id === confirmId)}
              busy={busy}
              onCancel={() => setConfirmId(null)}
              onConfirm={approve}
            />
          )}
        </div>
      )}
    </article>
  );
}

function OptionCard({ option, isSelected, disabled, onApprove }) {
  const [showAll, setShowAll] = useState(false);
  return (
    <div
      className={`rounded-2xl p-5 ring-2 transition relative ${
        isSelected
          ? 'ring-emerald-500 bg-emerald-50/60'
          : option.is_recommended
            ? 'ring-emerald-200 bg-white'
            : 'ring-slate-200 bg-white'
      }`}
      data-testid={`option-${option.id}`}
    >
      {isSelected && (
        <div className="absolute -top-2.5 right-3 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[10px] font-semibold uppercase">
          Approved
        </div>
      )}
      {!isSelected && option.is_recommended && (
        <div className="absolute -top-2.5 right-3 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[10px] font-semibold uppercase tracking-wide flex items-center gap-1">
          <Star className="w-3 h-3" /> Broker pick
        </div>
      )}

      <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">{option.label}</div>
      <div className="mt-1 font-bold text-slate-900 text-lg">{option.lender}</div>

      <div className="mt-3 grid grid-cols-2 gap-y-2 gap-x-3 text-sm">
        <KV label="Loan amount" value={formatCurrency(option.loan_amount)} />
        <KV label="Rate" value={`${Number(option.interest_rate).toFixed(2)}% p.a.`} />
        <KV label="Comparison" value={option.comparison_rate != null ? `${Number(option.comparison_rate).toFixed(2)}%` : '—'} />
        <KV label="Term" value={`${option.loan_term_years} yrs`} />
        <KV label="Type" value={option.loan_type || '—'} />
        <KV label="Monthly" value={option.repayment_monthly != null ? formatCurrency(option.repayment_monthly) : '—'} bold />
        <KV label="Fortnightly" value={option.repayment_fortnightly != null ? formatCurrency(option.repayment_fortnightly) : '—'} />
      </div>

      <div className="mt-3 flex items-center gap-2 flex-wrap text-[11px]">
        {option.offset && <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Offset</span>}
        {option.redraw && <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Redraw</span>}
        {option.fixed_variable_split && <span className="px-2 py-0.5 rounded-full bg-sky-100 text-sky-700">{option.fixed_variable_split}</span>}
        {option.cashback != null && option.cashback > 0 && (
          <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">{formatCurrency(option.cashback)} cashback</span>
        )}
        {option.lmi != null && option.lmi > 0 && (
          <span className="px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">LMI {formatCurrency(option.lmi)}</span>
        )}
      </div>

      {(option.upfront_fees || option.ongoing_fees) && (
        <div className="mt-3 text-xs text-slate-500">
          {option.upfront_fees != null && <span>Upfront fees {formatCurrency(option.upfront_fees)}</span>}
          {option.upfront_fees != null && option.ongoing_fees != null && <span> · </span>}
          {option.ongoing_fees != null && <span>Ongoing {formatCurrency(option.ongoing_fees)}/yr</span>}
        </div>
      )}

      {(option.pros?.length > 0 || option.cons?.length > 0 || option.reason_recommended) && (
        <button
          type="button"
          onClick={() => setShowAll(!showAll)}
          className="mt-3 text-xs text-emerald-700 hover:underline"
        >
          {showAll ? 'Hide details' : 'See pros, cons & broker notes'}
        </button>
      )}

      {showAll && (
        <div className="mt-3 space-y-3 text-sm">
          {option.pros?.length > 0 && (
            <div>
              <div className="flex items-center gap-1 text-emerald-700 text-xs font-semibold mb-1"><ThumbsUp className="w-3 h-3" /> Pros</div>
              <ul className="list-disc pl-5 text-slate-700 space-y-0.5">
                {option.pros.map((p, i) => <li key={i}>{p}</li>)}
              </ul>
            </div>
          )}
          {option.cons?.length > 0 && (
            <div>
              <div className="flex items-center gap-1 text-rose-700 text-xs font-semibold mb-1"><ThumbsDown className="w-3 h-3" /> Cons</div>
              <ul className="list-disc pl-5 text-slate-700 space-y-0.5">
                {option.cons.map((c, i) => <li key={i}>{c}</li>)}
              </ul>
            </div>
          )}
          {option.reason_recommended && (
            <div className="rounded-lg bg-emerald-50/60 ring-1 ring-emerald-200 p-3 text-slate-700">
              <div className="text-xs font-semibold text-emerald-700 mb-1">Why your broker recommends this</div>
              <div className="whitespace-pre-wrap">{option.reason_recommended}</div>
            </div>
          )}
        </div>
      )}

      {!isSelected && !disabled && (
        <button
          type="button"
          onClick={onApprove}
          data-testid={`approve-option-${option.id}`}
          className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold transition"
        >
          <CheckCircle2 className="w-4 h-4" />
          Approve this option
        </button>
      )}
    </div>
  );
}

function ApproveConfirm({ option, busy, onCancel, onConfirm }) {
  if (!option) return null;
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl ring-1 ring-slate-200 max-w-md w-full p-6 shadow-xl">
        <div className="font-semibold text-slate-900 text-lg">Approve this proposal?</div>
        <div className="text-slate-600 text-sm mt-1.5">
          You're approving <strong>{option.label}</strong> with <strong>{option.lender}</strong>.
        </div>
        <div className="mt-3 rounded-lg bg-slate-50 ring-1 ring-slate-200 p-3 text-sm space-y-1">
          <div className="flex justify-between"><span className="text-slate-500">Rate</span><span className="font-medium">{Number(option.interest_rate).toFixed(2)}%</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Monthly repayment</span><span className="font-semibold">{option.repayment_monthly != null ? formatCurrency(option.repayment_monthly) : '—'}</span></div>
        </div>
        <p className="text-xs text-slate-500 mt-3">
          By approving, you authorise your broker to start preparing and submitting your loan application based on this option.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onCancel} className="px-4 py-2 text-slate-500 hover:text-slate-900 text-sm">Cancel</button>
          <button
            onClick={onConfirm}
            disabled={busy}
            data-testid="approve-confirm"
            className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            Yes, approve this option
          </button>
        </div>
      </div>
    </div>
  );
}

function KV({ label, value, bold }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-slate-800 ${bold ? 'font-semibold' : ''}`}>{value}</div>
    </div>
  );
}
